import type {
  RestrictionRuleType,
  RestrictionSeverity,
} from "@/types/restrictions";

export const RESTRICTION_TYPE_INFO: Record<
  RestrictionRuleType,
  { label: string; description: string }
> = {
  minimum_profit: {
    label: "Minimum Profit",
    description:
      "Enforces a minimum savings threshold — either a flat amount, or container-count tiers compared against total or per-container savings",
  },
  days_before_departure: {
    label: "Days Before Departure",
    description: "Time-based eligibility with extra profit requirements",
  },
  days_before_stuffing: {
    label: "Days Before Stuffing",
    description:
      "Blocks when the search date is too close to the stuffing date, with optional per-weekday overrides",
  },
  blocked_supplier: {
    label: "Blocked Supplier",
    description: "Prevents booking with specific carriers",
  },
  blocked_end_client: {
    label: "Blocked Consignee",
    description:
      "Blocks first-book and/or rebook recommendations for specific consignees (customers)",
  },
  blocked_service_trade: {
    label: "Blocked Service Trade",
    description:
      "Blocks first-book and rebook recommendations for bookings on selected main-line service trades",
  },
  booking_state_blocked: {
    label: "Booking State Blocked",
    description:
      "Blocks recommendations for bookings in a specific lifecycle state",
  },
  preferred_supplier: {
    label: "Preferred Supplier",
    description: "Promotes booking with preferred carriers",
  },
  port_restriction: {
    label: "Port Restriction",
    description: "Route-specific rules for origin/destination ports",
  },
  cancellation_policy: {
    label: "Cancellation Policy",
    description: "Cutoff dates and cancellation fee rules",
  },
  equipment_restriction: {
    label: "Equipment Restriction",
    description: "Container type and size limitations",
  },
  commodity_restriction: {
    label: "Commodity Restriction",
    description: "Cargo type restrictions",
  },
  overweight_surcharge: {
    label: "Overweight Surcharge",
    description: "Tiered surcharges for overweight containers",
  },
  pcd_missing: {
    label: "PCD Missing",
    description: "Note when PCD is missing and ETD is used as fallback",
  },
  cutoff_missing: {
    label: "Cutoff Missing",
    description: "Warning when cutoff date is missing from agent data",
  },
  schedule_missing: {
    label: "Schedule Missing",
    description: "Warning when no sailing schedule is found for an alternative rate",
  },
  transshipment_limit: {
    label: "Transshipment Limit",
    description: "Limits the number of transshipment legs on matched schedules",
  },
  transshipment_change: {
    label: "Transshipment Change",
    description:
      "Bonus when rebook reduces transshipment legs; warning when it adds legs",
  },
  contract_missing: {
    label: "Contract Missing",
    description: "Blocks when no original contract reference is found",
  },
  allocation_over_limit: {
    label: "Allocation Over Limit",
    description: "Warns or blocks when allocation utilization exceeds thresholds",
  },
  match_type_blocked: {
    label: "Match Type Blocked",
    description: "Blocks recommendations by match type (e.g. Upgrade, Mapped)",
  },
  weight_limit: {
    label: "Weight Limit",
    description: "Blocks when cargo weight exceeds the rate's maximum weight",
  },
  cutoff_passed: {
    label: "Cutoff Date Passed",
    description: "Blocks when the cargo cutoff date has already passed",
  },
  stuffing_passed: {
    label: "Stuffing Date Passed",
    description: "Blocks when the stuffing date has already passed",
  },
  stuffing_missing: {
    label: "Stuffing Date Missing",
    description: "Note when stuffing date is missing from agent data",
  },
  dg_restriction: {
    label: "DG Cargo Blocked",
    description: "Blocks opportunities for bookings tagged as Dangerous Goods",
  },
  rate_validity_expiring: {
    label: "Rate Validity Expiring",
    description:
      "Blocks recommendations whose rate validity ends too close to the search date",
  },
  ready_date_rate_validity: {
    label: "Rate Valid Through Ready Date",
    description:
      "Guards ready-date rate feasibility: warns when valid_from is soon after ready date, blocks when validity ends too soon or start is beyond grace",
  },
  container_weight_missing: {
    label: "Container Weight Missing",
    description: "Note when container weights are missing",
  },
  agent_rebook_limit: {
    label: "Agent Rebook Limit",
    description:
      "Blocks when the booking's agent already exceeded the rebook threshold",
  },
  carrier_rebook_limit: {
    label: "Carrier Rebook Limit",
    description:
      "Blocks when the booking's carrier already exceeded the rebook threshold",
  },
  contract_rebook_limit: {
    label: "Contract Rebook Limit",
    description:
      "Blocks when the booking's contract already exceeded the rebook threshold",
  },
};

export const SEVERITY_LABELS: Record<RestrictionSeverity, string> = {
  blocked: "Blocked",
  warning: "Warning",
  info: "Info",
  bonus: "Bonus",
};

export function restrictionTypeLabel(ruleType: string): string {
  return (
    RESTRICTION_TYPE_INFO[ruleType as RestrictionRuleType]?.label ?? ruleType
  );
}

export function restrictionTypeDescription(ruleType: string): string | null {
  return (
    RESTRICTION_TYPE_INFO[ruleType as RestrictionRuleType]?.description ?? null
  );
}

function formatParamKey(key: string): string {
  return key
    .split("_")
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join(" ");
}

/** Compact, human-readable restriction parameter value. */
export function formatParamValue(value: unknown): string {
  if (value === null || value === undefined || value === "") return "—";
  if (typeof value === "boolean") return value ? "True" : "False";
  if (typeof value === "number" || typeof value === "string") {
    return String(value);
  }
  if (Array.isArray(value)) {
    if (value.length === 0) return "—";
    if (value.every((v) => typeof v !== "object" || v === null)) {
      return value.map((v) => formatParamValue(v)).join(", ");
    }
    return JSON.stringify(value);
  }
  if (typeof value === "object") {
    const entries = Object.entries(value as Record<string, unknown>);
    if (entries.length === 0) return "—";
    return entries
      .map(([k, v]) => `${formatParamKey(k)}: ${formatParamValue(v)}`)
      .join(", ");
  }
  return JSON.stringify(value);
}

export function formatRestrictionParams(
  params: Record<string, unknown>,
): string {
  const entries = Object.entries(params).filter(
    ([, v]) => v !== null && v !== undefined && v !== "",
  );
  if (entries.length === 0) return "—";
  return entries
    .map(([k, v]) => `${formatParamKey(k)}: ${formatParamValue(v)}`)
    .join(" · ");
}
